import { useState, type ReactNode } from 'react';
import { Button, Card, CardHeader, EmptyState, IconInfo, cn } from '@/design-system';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GRÁFICOS ACESSÍVEIS DOS INDICADORES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * POR QUE NÃO HÁ BIBLIOTECA DE GRÁFICO AQUI
 * Um `<canvas>` é opaco para tecnologia assistiva e some na impressão em preto
 * e branco. Os indicadores de SGQ são o insumo da ANÁLISE CRÍTICA PELA DIREÇÃO
 * (ISO 13485 §5.6) e vão impressos para a reunião — um gráfico que não imprime
 * é um indicador que não existe naquele momento.
 *
 * Aqui a barra é HTML puro e marcada `aria-hidden`: ela é REFORÇO visual. O
 * dado em si é uma tabela de verdade, com `<caption>`, `<th scope>` e valores
 * numéricos, alcançável por qualquer leitor de tela — e é a mesma tabela que a
 * exportação CSV produz. O botão "Ver como tabela" alterna a visualização para
 * quem prefere ler os números, inclusive com mouse.
 *
 * É a mesma decisão de `DocumentsByTypeChart` no painel do GED, generalizada.
 */

export interface ChartDatum {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  /** Percentual já calculado pelo servidor. Ausente = calculado sobre o total. */
  readonly percentage?: number;
  /** Realce opcional (ex.: faixa de criticidade alta). */
  readonly emphasis?: 'neutral' | 'danger' | 'warning' | 'success';
}

const BAR_TONE: Record<NonNullable<ChartDatum['emphasis']>, string> = {
  neutral: 'bg-brand',
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
};

export interface BarChartProps {
  readonly title: string;
  readonly description?: string;
  readonly data: readonly ChartDatum[];
  /** Nome da coluna de categoria na tabela alternativa. */
  readonly categoryHeader: string;
  readonly valueHeader?: string;
  readonly emptyTitle?: string;
  readonly emptyDescription?: string;
  readonly action?: ReactNode;
  readonly className?: string;
}

/**
 * Barras horizontais com tabela alternativa obrigatória.
 *
 * A tabela NÃO é opcional para o leitor de tela: ela está sempre no DOM. O
 * botão apenas troca qual das duas representações fica visível — quando a
 * tabela está oculta, ela é `qc-sr-only`, não `display:none`.
 */
export function BarChart({
  title,
  description,
  data,
  categoryHeader,
  valueHeader = 'Quantidade',
  emptyTitle = 'Sem dados no período',
  emptyDescription = 'Ajuste o período ou os filtros para ver este indicador.',
  action,
  className,
}: BarChartProps) {
  const [showTable, setShowTable] = useState(false);

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const max = data.reduce((peak, item) => Math.max(peak, item.value), 0);

  return (
    <Card className={cn('flex min-w-0 flex-col', className)}>
      <CardHeader
        headingLevel={3}
        title={title}
        description={description}
        action={
          <div className="flex items-center gap-2">
            {action}
            {data.length > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                aria-pressed={showTable}
                onClick={() => setShowTable((current) => !current)}
              >
                {showTable ? 'Ver como gráfico' : 'Ver como tabela'}
              </Button>
            ) : null}
          </div>
        }
      />

      {data.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={<IconInfo size={20} />}
          title={emptyTitle}
          description={emptyDescription}
          className="mt-2"
        />
      ) : (
        <div className="mt-4 min-w-0">
          {/* Representação VISUAL. Escondida do leitor de tela: os mesmos
              números estão na tabela abaixo, num formato navegável. */}
          <div aria-hidden="true" className={cn('flex flex-col gap-2.5', showTable && 'hidden')}>
            {data.map((item) => {
              const percent = item.percentage ?? (total === 0 ? 0 : (item.value / total) * 100);
              return (
                <div key={item.key}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-sm text-content">{item.label}</span>
                    <span className="shrink-0 text-sm font-medium text-content qc-tabular">
                      {item.value}
                      <span className="ml-1 text-xs font-normal text-content-subtle">
                        ({formatPercent(percent)})
                      </span>
                    </span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className={cn('h-full rounded-full', BAR_TONE[item.emphasis ?? 'neutral'])}
                      style={{ width: `${max === 0 ? 0 : (item.value / max) * 100}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Representação DE DADO. Sempre no DOM; o botão só decide se ela
              também aparece na tela. */}
          <div className={cn('overflow-x-auto', showTable ? 'block' : 'qc-sr-only')}>
            <table className="w-full border-collapse text-sm">
              <caption className="qc-sr-only">
                {title}
                {description ? `. ${description}` : ''}
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    {categoryHeader}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    {valueHeader}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    Participação
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => {
                  const percent = item.percentage ?? (total === 0 ? 0 : (item.value / total) * 100);
                  return (
                    <tr key={item.key} className="border-b border-border-subtle last:border-b-0">
                      <th scope="row" className="px-3 py-2 text-left font-medium text-content">
                        {item.label}
                      </th>
                      <td className="px-3 py-2 text-right text-content qc-tabular">{item.value}</td>
                      <td className="px-3 py-2 text-right text-content-muted qc-tabular">
                        {formatPercent(percent)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-surface-sunken">
                  <th scope="row" className="px-3 py-2 text-left font-semibold text-content">
                    Total
                  </th>
                  <td className="px-3 py-2 text-right font-semibold text-content qc-tabular">
                    {total}
                  </td>
                  <td className="px-3 py-2 text-right text-content-muted qc-tabular">100,0%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Série temporal
// ─────────────────────────────────────────────────────────────────────────────

export interface TrendPoint {
  readonly period: string;
  readonly opened: number;
  readonly closed: number;
}

/**
 * Tendência de abertura × encerramento de NC.
 *
 * Duas barras por período, empilhadas verticalmente por leitura de esquerda
 * para a direita. A tabela alternativa traz também o SALDO (abertas menos
 * encerradas), que é o número que interessa na análise crítica e que um gráfico
 * de duas barras obriga o leitor a calcular de cabeça.
 */
export function TrendChart({
  title,
  description,
  data,
  className,
}: {
  readonly title: string;
  readonly description?: string;
  readonly data: readonly TrendPoint[];
  readonly className?: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const max = data.reduce((peak, item) => Math.max(peak, item.opened, item.closed), 0);

  return (
    <Card className={cn('flex min-w-0 flex-col', className)}>
      <CardHeader
        headingLevel={3}
        title={title}
        description={description}
        action={
          data.length > 0 ? (
            <Button
              size="sm"
              variant="ghost"
              aria-pressed={showTable}
              onClick={() => setShowTable((current) => !current)}
            >
              {showTable ? 'Ver como gráfico' : 'Ver como tabela'}
            </Button>
          ) : undefined
        }
      />

      {data.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={<IconInfo size={20} />}
          title="Sem série temporal no período"
          description="Nenhuma não-conformidade foi aberta ou encerrada no intervalo escolhido."
          className="mt-2"
        />
      ) : (
        <div className="mt-4 min-w-0">
          <div aria-hidden="true" className={cn('flex flex-col gap-3', showTable && 'hidden')}>
            {/* Legenda com TEXTO, não só cor. */}
            <p className="flex flex-wrap items-center gap-4 text-xs text-content-muted">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-warning" />
                Abertas
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-success" />
                Encerradas
              </span>
            </p>

            {data.map((point) => (
              <div key={point.period}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-content">{point.period}</span>
                  <span className="text-xs text-content-muted qc-tabular">
                    {point.opened} abertas · {point.closed} encerradas
                  </span>
                </div>
                <div className="mt-1 flex flex-col gap-1">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className="h-full rounded-full bg-warning"
                      style={{ width: `${max === 0 ? 0 : (point.opened / max) * 100}%` }}
                    />
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className="h-full rounded-full bg-success"
                      style={{ width: `${max === 0 ? 0 : (point.closed / max) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={cn('overflow-x-auto', showTable ? 'block' : 'qc-sr-only')}>
            <table className="w-full border-collapse text-sm">
              <caption className="qc-sr-only">
                {title}
                {description ? `. ${description}` : ''}
              </caption>
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    Período
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    Abertas
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    Encerradas
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-muted"
                  >
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((point) => {
                  const balance = point.opened - point.closed;
                  return (
                    <tr key={point.period} className="border-b border-border-subtle last:border-b-0">
                      <th scope="row" className="px-3 py-2 text-left font-medium text-content">
                        {point.period}
                      </th>
                      <td className="px-3 py-2 text-right text-content qc-tabular">
                        {point.opened}
                      </td>
                      <td className="px-3 py-2 text-right text-content qc-tabular">
                        {point.closed}
                      </td>
                      <td className="px-3 py-2 text-right text-content qc-tabular">
                        {balance > 0 ? `+${balance}` : String(balance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function formatPercent(value: number): string {
  return `${value.toFixed(1).replace('.', ',')}%`;
}
